import React, { Component } from 'react';
import { Card } from 'rebass';
import { Button, Modal, Divider, Grid, Card as CardSemantic, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { Form, Input, Dropdown } from 'formsy-semantic-ui-react';
import { addValidationRule } from 'formsy-react';
import { rgba } from 'polished';
import { withApollo } from 'react-apollo';

import { Box, Flex, Text } from 'Components/Base';
import { SPACE, FONT_SIZE } from 'Components/Theme';
import { MAJOR_OPTIONS } from 'Components/AddPersonalInformation/AddPersonalInformation';
import { mockExamSchoolStatus } from 'GraphQL/query/mockSchool/MockExamSchoolStatus.query';
import { mockExamSchools } from 'GraphQL/query/mockSchool/MockExamSchools.query';
import { thaiRegularExpressionWithOutNumber, errorLabel } from 'Util/form';

import LinkSchoolErrorModal from './LinkSchoolErrorModal';
import LinkSchoolErrorContact from './LinkSchoolErrorContact';
import LinkSchoolManageModal from './LinkSchoolManageModal';

const DropdownField = styled(Form.Field)`
  width: 100%;
  &&& {
    margin-bottom: ${SPACE.small}px;
  }
`;

const FWField = styled(Form.Field)`
  width: 100%;
  max-width: 216px !important;
  min-width: 200px !important;
`;

const LabelForm = styled(Text)`
  text-align: right;
  font-weight: 500;
`;

const Field = props => {
  return (
    <Box css={{ width: '80%', maxWidth: '312px' }}>
      <Flex justifyContent="center" alignItems="baseline" mb={2} p={2} flexDirection="row">
        {props.children}
      </Flex>
    </Box>
  );
};

const LabelText = props => (
  <Box css={{ minWidth: '140px', textAlign: 'right' }}>
    <Text pr={4}>{props.children}</Text>
  </Box>
);

const ModalField = ({ label, value }) => (
  <Grid.Row columns={4} centered>
    <Grid.Column>
      <LabelForm>{label}</LabelForm>
    </Grid.Column>
    <Grid.Column>
      <Text>{value}</Text>
    </Grid.Column>
  </Grid.Row>
);

addValidationRule('isThai', function(values, value) {
  return thaiRegularExpressionWithOutNumber.test(value); // You can return an error
});

addValidationRule('isSSID', function(values, value) {
  let sum = 0;
  if (value === undefined) return true;
  // SSID rule will support some special case ex.0080011050306, 0907789000049
  // but not for random value '0000000000000'
  // issue card: https://skooldio.atlassian.net/browse/TCMF-342
  if (value.length !== 13 || value.substring(0, 3) === '000') return false;
  if (value[0] === '0') return true;
  for (let i = 0; i < 12; i++) sum += parseFloat(value.charAt(i)) * (13 - i);
  if ((11 - (sum % 11)) % 10 != parseFloat(value.charAt(12))) return false;
  return true;
});

class LinkSchoolData extends Component {
  state = {
    schoolList: [],
    open: false,
    renderForm: false,
    renderInformSchool: false,
    locationCode: this.props.locationCode,
    locationName: this.props.locationName,
    openErrorModal: false,
    openManageModal: false,
  };

  componentDidMount = async () => {
    const { locationName, mockExamStudentProfile } = this.props;
    if (locationName) {
      this.queryMockExamSchoolStatus();
    }
    if (mockExamStudentProfile) {
      this.openManageModal();
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.openErrorModal === false && this.props.openErrorModal === true) {
      this.openErrorModal();
    }
  }

  onCheckValid = formData => {
    if (this.state.renderForm) {
      let invalidName = false;
      const { firstName, lastName } = this.props.studentProfile;
      if (formData.firstName !== firstName || formData.lastName !== lastName) {
        invalidName = true;
      }
      this.setState(
        {
          data: formData,
          invalidName,
        },
        () => this.setState({ open: true }),
      );
    }
  };

  handleEditDataOnModal = () => {
    this.setState({
      open: false,
    });
  };

  handleSendDataOnModal = () => {
    // check roundcode and location code
    const { roundCode } = this.props;
    const {
      locationCode,
      data: { firstName, lastName, ssid, phone, email, major, studentCode },
    } = this.state;
    const studentData = {
      firstName: firstName || '',
      lastName: lastName || '',
      ssid: ssid || '',
      phone: phone || '',
      email: email || '',
      major: major || '',
      studentCode: studentCode || '',
    };

    this.props.mutation({
      variables: { roundCode, locationCode, studentData },
    });
    this.setState({
      open: false,
    });
  };

  handleOnChange = async (e, { value }) => {
    const { schoolList } = this.state;
    const { locationName } = schoolList.find(({ locationCode }) => {
      return locationCode === value;
    });
    this.setState({ locationCode: value, locationName }, () => this.queryMockExamSchoolStatus());
  };

  fetchSchool = async (e, { searchQuery }) => {
    if (searchQuery.length < 3) return null;
    const { client } = this.props;
    const res = await client.query({
      query: mockExamSchools,
      variables: { searchLocation: searchQuery },
    });
    const schools = res ? res.data.mockExamSchools : {};
    const schoolList = schools.map(school => {
      const { locationCode, locationName } = school;
      return { locationCode, locationName };
    });
    this.setState({
      schoolList: schoolList ? schoolList : [],
    });
  };

  queryMockExamSchoolStatus() {
    const { client, roundCode } = this.props;
    const { locationCode } = this.state;

    client
      .query({
        query: mockExamSchoolStatus,
        variables: { roundCode, locationCode },
      })
      .then(res => {
        const { availableExams } =
          res.data && res.data.mockExamSchoolStatus ? res.data.mockExamSchoolStatus : [];
        if (availableExams && availableExams.length) {
          this.setState({ renderForm: true, renderInformSchool: false });
        } else {
          this.setState({ renderInformSchool: true, renderForm: false });
        }
      });
  }

  openErrorModal = () => {
    this.setState({
      openErrorModal: true,
    });
  };

  closeErrorModal = () => {
    this.setState({
      openErrorModal: false,
    });
  };

  openManageModal = () => {
    this.setState({
      openManageModal: true,
    });
  };

  closeManageModal = () => {
    this.setState({
      openManageModal: false,
    });
  };

  handleManageExamClick = () => {
    const { history, roundCode } = this.props;
    history.push(`/school/manage-mock-exam/${encodeURIComponent(roundCode)}`);
  };

  renderSchoolInformation() {
    return (
      <Card
        fontSize={6}
        fontWeight="bold"
        css={{
          minWidth: '320px',
          overflow: 'visible !important',
        }}
        width={[1, 1, 2 / 3]}
        py={5}
        m={8}
        mt={20}
        borderRadius={2}
      >
        <Flex justifyContent="center" alignItems="baseline" flexDirection="row">
          <Text pr={3}>โรงเรียน</Text>
          <Box>
            <DropdownField>
              {this.props.locationName ? (
                <Input name="locationCode" disabled defaultValue={this.props.locationName || ''} />
              ) : (
                <Dropdown
                  selection
                  search
                  fluid
                  scrolling
                  noResultsMessage={null}
                  onSearchChange={this.fetchSchool}
                  onChange={this.handleOnChange}
                  options={this.state.schoolList.map(school => {
                    const { locationCode, locationName } = school;
                    return { text: locationName, value: locationCode };
                  })}
                  name="locationCode"
                  placeholder="โรงเรียน"
                  required
                  validationErrors={{
                    isDefaultRequiredValue: 'กรุณาใส่ชื่อโรงเรียนของคุณ',
                  }}
                  errorLabel={errorLabel}
                />
              )}
            </DropdownField>
            <Text fontSize={FONT_SIZE.small} color="greyscale.grey3">
              พิมพ์ชื่อโรงเรียนอย่างน้อย 3 ตัวอักษร เพื่อทำการค้นหา
            </Text>
          </Box>
        </Flex>
      </Card>
    );
  }

  renderPersonalInformation() {
    const { studentProfile, educationProfile } = this.props;
    return (
      <Card
        fontSize={6}
        fontWeight="bold"
        css={{
          minWidth: '320px',
        }}
        width={[1, 1, 2 / 3]}
        p={5}
        m={8}
        borderRadius={2}
      >
        <Flex alignItems="center" justifyContent="center" mb={2} flexDirection="column">
          <Field>
            <LabelText>ชื่อ</LabelText>
            <FWField>
              <Input
                name="firstName"
                validations={{ isThai: true }}
                validationErrors={{
                  isThai: 'กรุณากรอกชื่อของคุณเป็นภาษาไทย',
                }}
                placeholder=""
                errorLabel={errorLabel}
                defaultValue={studentProfile.firstName || ''}
              />
            </FWField>
          </Field>

          <Field>
            <LabelText>นามสกุล</LabelText>
            <FWField>
              <Input
                name="lastName"
                validations={{ isThai: true }}
                validationErrors={{
                  isThai: 'กรุณากรอกนามสกุลของคุณเป็นภาษาไทย',
                }}
                placeholder=""
                errorLabel={errorLabel}
                defaultValue={studentProfile.lastName || ''}
              />
            </FWField>
          </Field>

          <Field>
            <LabelText>แผนการเรียน</LabelText>
            <FWField>
              <Dropdown
                selection
                validations="isExisty"
                validationError="กรุณาเลือกแผนการเรียน"
                name="major"
                options={MAJOR_OPTIONS}
                errorLabel={errorLabel}
                defaultValue={educationProfile ? educationProfile.major : ''}
              />
            </FWField>
          </Field>

          <Field>
            <LabelText>รหัสนักเรียน</LabelText>
            <FWField>
              <Input
                name="studentCode"
                type="number"
                validations="isNumeric"
                validationErrors={{
                  isInt: 'กรุณากรอกรหัสนักเรียนเป็นตัวเลข',
                }}
                placeholder=""
                errorLabel={errorLabel}
              />
            </FWField>
          </Field>
          <Field>
            <LabelText>เลขบัตรประชาชน</LabelText>
            <FWField>
              <Input
                name="ssid"
                type="number"
                validations={{ isNumeric: true, isLength: 13, isSSID: true }}
                validationErrors={{
                  isNumeric: 'กรุณากรอกรหัสบัตรประชาชนเป็นตัวเลข',
                  isSSID: 'กรุณากรอกเลขบัตรประชาขนให้ถูกต้อง',
                  isLength: 'กรุณากรอกเลขบัตรประชาชนให้ครบ 13 ตัวเลข',
                }}
                placeholder=""
                errorLabel={errorLabel}
              />
            </FWField>
          </Field>
          <Text
            pt={20}
            style={{ fontWeight: 500, display: 'inline-block', minWidth: '250px' }}
            mt={SPACE.medium}
          >
            เพื่อการประกาศผล เราขอข้อมูลดังต่อไปนี้
          </Text>
          <Field>
            <LabelText>เบอร์โทรศัพท์</LabelText>
            <FWField>
              <Input
                name="phone"
                type="tel"
                required
                validations={{ minLength: 9, isNumeric: true, maxLength: 12 }}
                validationErrors={{
                  maxLength: 'กรุณากรอกเบอร์โทรศัพท์ให้ครบ 9-12 ตัว',
                  minLength: 'กรุณากรอกเบอร์โทรศัพท์ให้ครบ 9-12 ตัว',
                  isNumeric: 'กรุณากรอกเบอร์โทรศัพท์เป็นตัวเลข',
                  isDefaultRequiredValue: 'กรุณากรอกเบอร์โทรศัพท์',
                }}
                placeholder=""
                errorLabel={errorLabel}
              />
            </FWField>
          </Field>
          <Field>
            <LabelText pr={4}>อีเมล</LabelText>
            <FWField>
              <Input
                name="email"
                required
                validations="isEmail"
                validationErrors={{
                  isEmail: 'อีเมลต้องอยู่ในรูปแบบของ username@domain.com',
                  isDefaultRequiredValue: 'กรุณากรอกอีเมล',
                }}
                placeholder=""
                errorLabel={errorLabel}
              />
            </FWField>
          </Field>
          <Button
            primary
            circular
            size="medium"
            type="submit"
            background-color="#ff5855"
            content={<span style={{ fontWeight: 400 }}>เชื่อมข้อมูลกับ TCASter</span>}
          />
        </Flex>
      </Card>
    );
  }

  renderInvalidName() {
    const { firstName, lastName } = this.props.studentProfile;
    return (
      <CardSemantic
        fluid
        style={{
          alignItems: 'center',
          padding: '15px',
          margin: '30px',
          backgroundColor: rgba(251, 170, 25, 0.1),
          border: 'solid 2px #fbaa19',
        }}
      >
        <Icon name="warning sign" size="huge" style={{ marginBottom: '10px' }} />
        <Text style={{ whiteSpace: 'pre-line' }}>
          {'ชื่อล๊อคอินของน้องคือ '}
          <Text
            style={{
              fontWeight: 500,
              display: 'inline-block',
              color: 'red',
            }}
          >
            {`“${firstName} ${lastName}”`}
          </Text>
          {'\nไม่ตรงกับชื่อนามสกุลที่กรอกมา\n'}
          <Text
            style={{
              fontWeight: 500,
              display: 'inline-block',
              color: 'red',
            }}
          >
            กรุณาตรวจสอบอีกครั้ง
          </Text>
        </Text>
      </CardSemantic>
    );
  }

  renderConfirmModal() {
    const {
      data: { email, firstName, lastName, major, ssid, locationCode, studentCode, phone } = {},
    } = this.state;
    const school = locationCode
      ? this.state.schoolList.find(school => school.locationCode === locationCode)
      : '';
    return (
      <Modal style={{ textAlign: 'center' }} size="tiny" open={this.state.open}>
        <Modal.Header>
          <Text style={{ fontWeight: 500, fontSize: 16 }}>ยืนยันข้อมูล</Text>
        </Modal.Header>
        <Modal.Content>
          <Modal.Description style={{ paddingBottom: '20px' }}>
            <Grid centered>
              <Grid.Row>
                {this.state.invalidName ? (
                  this.renderInvalidName()
                ) : (
                  <Text
                    style={{
                      fontWeight: 500,
                      display: 'inline-block',
                      color: 'red',
                    }}
                  >
                    กรุณาตรวจสอบข้อมูลให้ละเอียดอีกครั้งก่อนกดยืนยัน
                  </Text>
                )}
              </Grid.Row>
              <ModalField
                label={'โรงเรียน'}
                value={school ? school.locationName : this.props.locationName || ''}
              />
              <ModalField label={'ชื่อ'} value={firstName || ''} />
              <ModalField label={'นามสกุล'} value={lastName || ''} />
              <ModalField label={'แผนการเรียน'} value={major || ''} />
              <ModalField label={'รหัสนักเรียน'} value={studentCode || ''} />
              <ModalField label={'เลขบัตรประชาชน'} value={ssid || ''} />
              <Divider />
              <ModalField label={'เบอร์โทรศัพท์'} value={phone || ''} />
              <ModalField label={'อีเมล'} value={email || ''} />
            </Grid>
          </Modal.Description>
          <Button
            basic
            circular
            style={{
              minWidth: '150px',
              height: '40px',
              backgroundColor: '#ffffff',
              border: '#9b9b9b',
              margin: '5px 20px',
            }}
            content={<span style={{ fontWeight: 400 }}>กลับไปแก้ไขข้อมูล</span>}
            onClick={this.handleEditDataOnModal}
          />
          <Button
            primary
            circular
            style={{ minWidth: '150px', height: '40px', margin: '5px 20px' }}
            content={<span style={{ fontWeight: 400 }}>ยืนยันข้อมูล</span>}
            onClick={this.handleSendDataOnModal}
          />
        </Modal.Content>
      </Modal>
    );
  }

  renderErrorModal() {
    const error = this.props.error;
    const { openErrorModal, locationName } = this.state;
    return (
      <LinkSchoolErrorModal
        error={error}
        open={openErrorModal}
        onClose={this.closeErrorModal}
        locationName={locationName}
      />
    );
  }

  renderManageModal() {
    const { openManageModal } = this.state;
    const { mockExamStudentProfile } = this.props;
    return (
      <LinkSchoolManageModal
        open={openManageModal}
        onClose={this.closeManageModal}
        mockExamStudentProfile={mockExamStudentProfile}
        onManageExamClick={this.handleManageExamClick}
      />
    );
  }

  render() {
    const { renderForm, renderInformSchool } = this.state;
    const { roundTitle } = this.props;
    return (
      <Form onValidSubmit={this.onCheckValid}>
        <Flex flexDirection={'column'} alignItems={'center'}>
          {this.renderSchoolInformation()}
          {renderForm &&
            ((
              <Box>
                <Text style={{ fontWeight: 500, display: 'inline-block' }} mt={SPACE.medium}>
                  กรุณากรอกข้อมูลให้ครบถ้วน
                </Text>
              </Box>
            ),
            this.renderPersonalInformation())}
          {renderInformSchool && (
            <LinkSchoolErrorContact
              errorText={`โรงเรียนดังกล่าวยังไม่ได้ทำการสมัคร Mock Exam ในรอบ ${roundTitle}`}
            />
          )}
        </Flex>
        {this.renderConfirmModal()}
        {this.renderErrorModal()}
        {this.renderManageModal()}
      </Form>
    );
  }
}

export default withApollo(LinkSchoolData);
